import { useState } from 'react';
import { useDisguise } from '../context/DisguiseContext';

// 글자 크기 단계
const THEME_FONT = {
  S: { name: 11, rate: 11, stock: 11, label: 13 },
  M: { name: 13, rate: 13, stock: 12, label: 14 },
  L: { name: 14, rate: 14, stock: 13, label: 15 },
};

export default function ThemeGroup({ themes }) {
  const [expanded, setExpanded] = useState(null);
  const { disguised, excel } = useDisguise();

  // 글자 크기 (localStorage 저장)
  const [sizeKey, setSizeKey] = useState(
    () => localStorage.getItem('themegroup_fontsize') || 'M'
  );
  const fs = THEME_FONT[sizeKey];
  function cycleSize(next) {
    setSizeKey(next);
    localStorage.setItem('themegroup_fontsize', next);
  }

  const [showAll, setShowAll] = useState(false);  // 테마 전체 보기 토글

  function toggle(i) {
    setExpanded(prev => prev === i ? null : i);
  }

  // 모드별 색상
  const upColor   = excel ? '#000000' : disguised ? '#9ca3af' : '#f85149';
  const downColor = excel ? '#666666' : disguised ? '#6b7280' : '#3b82f6';

  // 엑셀 모드 스타일 변수
  const itemBg  = excel ? '#f9f9f9' : '#161b22';
  const borderC = excel ? '#d0d0d0' : '#21262d';
  const barBgC  = excel ? '#e0e0e0' : '#21262d';
  const labelC  = excel ? '#000'    : '#e6edf3';
  const mutedC  = excel ? '#555'    : '#8b949e';
  const stockC  = excel ? '#000'    : '#c9d1d9';

  // 상위 절반만 노출 (더보기로 전체)
  const half = Math.max(1, Math.ceil(themes.length / 2));
  const visibleThemes = showAll ? themes : themes.slice(0, half);

  return (
    <div style={styles.panel}>
      <div style={{ ...styles.bar, borderBottom: `1px solid ${borderC}`, fontFamily: excel ? "'Calibri','Arial',sans-serif" : 'inherit' }}>
        <span style={{ ...styles.label, fontSize: fs.label, color: labelC, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {(!excel && !disguised) && (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#3fb950" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
              <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
              <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
              <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
            </svg>
          )}
          {excel ? '📊 분기 섹터 현황' : disguised ? '📊 Sector Overview' : '테마 동향'}
        </span>
        <div style={{ display: 'flex', gap: 3, marginLeft: 'auto' }}>
          {['S','M','L'].map(k => (
            <button key={k} onClick={() => cycleSize(k)} style={{
              fontSize: 14, padding: '2px 9px', borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${borderC}`,
              background: sizeKey === k ? (excel ? '#217346' : '#388bfd') : (excel ? '#fff' : '#21262d'),
              color: sizeKey === k ? '#fff' : (excel ? '#333' : '#8b949e'),
            }}>{k}</button>
          ))}
        </div>
      </div>
      <div style={styles.list}>
        {visibleThemes.map((t, i) => {
          const up   = t.change_rate >= 0;
          const barW = Math.min(Math.abs(t.change_rate) * 10, 100);
          const isOpen = expanded === i;
          const color  = up ? upColor : downColor;

          return (
            <div key={i} style={{ ...styles.item, background: itemBg, border: excel ? `1px solid ${borderC}` : 'none' }}>
              {/* 테마 헤더 */}
              <div
                style={{ ...styles.row, cursor: 'pointer' }}
                onClick={() => toggle(i)}
              >
                <span style={{ ...styles.name, fontSize: fs.name }}>
                  {isOpen ? '▼' : '▶'} {excel ? `분야-${String(i+1).padStart(2,'0')}` : t.theme_name}
                  {t.stock_count > 0 && (
                    <span style={styles.countBadge}>{excel ? `${t.stock_count}건` : `${t.stock_count}개`}</span>
                  )}
                </span>
                <span style={{ ...styles.rate, fontSize: fs.rate, color }}>
                  {up ? '+' : ''}{t.change_rate?.toFixed(2)}%
                </span>
              </div>

              {/* 등락률 바 */}
              <div style={{ ...styles.barBg, background: barBgC }}>
                <div style={{
                  height: '100%', borderRadius: 2,
                  width: `${barW}%`,
                  background: color,
                  transition: 'width 0.3s',
                }} />
              </div>

              {/* 포함 종목 (펼치기) */}
              {isOpen && t.stocks && t.stocks.length > 0 && (
                <div style={{ ...styles.stockList, borderTop: `1px solid ${borderC}` }}>
                  {t.stocks.map((s, j) => {
                    const sUp = s.change_rate >= 0;
                    const sColor = sUp ? upColor : downColor;
                    return (
                      <div key={j} style={styles.stockRow}>
                        <span style={{ ...styles.stockName, fontSize: fs.stock, color: stockC }}>{excel ? `항목-${String(j+1).padStart(2,'0')}` : s.name}</span>
                        <span style={{ ...styles.stockRate, fontSize: fs.stock, color: sColor }}>
                          {sUp ? '+' : ''}{s.change_rate?.toFixed(2)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {isOpen && (!t.stocks || t.stocks.length === 0) && (
                <div style={{ fontSize: 11, color: mutedC, padding: '4px 0' }}>
                  {excel ? '(데이터 없음)' : '종목 정보 없음'}
                </div>
              )}
            </div>
          );
        })}
        {themes.length > half && (
          <button
            onClick={() => setShowAll(v => !v)}
            style={{
              marginTop: 2, padding: '6px 0', width: '100%',
              fontSize: fs.name, cursor: 'pointer', borderRadius: 6,
              border: `1px solid ${borderC}`,
              background: excel ? '#fff' : '#161b22',
              color: mutedC, fontWeight: 600,
            }}
          >
            {showAll ? '접기 ▲' : `더보기 (+${themes.length - half}) ▼`}
          </button>
        )}
        {themes.length === 0 && (
          <div style={{ color: mutedC, fontSize: 12, textAlign: 'center', padding: 20 }}>
            {excel ? '불러오는 중...' : '데이터 로딩 중...'}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  panel:      { height: '100%', display: 'flex', flexDirection: 'column' },
  bar:        { display: 'flex', alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #21262d' },
  label:      { fontWeight: 700, fontSize: 14 },
  list:       { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 },
  item:       { background: '#161b22', borderRadius: 6, padding: '8px 12px' },
  row:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  name:       { fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 },
  rate:       { fontSize: 13, fontWeight: 700 },
  barBg:      { height: 4, background: '#21262d', borderRadius: 2, marginBottom: 4 },
  countBadge: { fontSize: 10, color: '#8b949e', fontWeight: 400, background: '#21262d', padding: '1px 5px', borderRadius: 3 },
  stockList:  { marginTop: 6, borderTop: '1px solid #21262d', paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 3 },
  stockRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 4px' },
  stockName:  { fontSize: 12, color: '#c9d1d9' },
  stockRate:  { fontSize: 12, fontWeight: 600 },
};
